import { reachGoal } from "./metrika";
import { trackerState } from "./state";
import { flushNow, queueEvent } from "./transport";

/**
 * A page can render several independent form instances (the guide pages carry a
 * hero form AND a bottom form), and every friction event they emit was
 * previously indistinguishable — same `field` kinds, same `page_url`, same
 * `event_slug`. `form_variant` is the per-instance discriminator that makes
 * hero-vs-bottom attributable in `interactions`. Omitted entirely when the
 * caller has only one form on the page, so existing rows stay comparable.
 */
function withFormVariant(
  data: Record<string, unknown>,
  formVariant?: string,
): Record<string, unknown> {
  return formVariant ? { ...data, form_variant: formVariant } : data;
}

export function trackCheckoutOpen(tier: string): void {
  trackerState.checkoutOpenTs = Date.now();
  queueEvent("checkout_open", { tier, visit_number: trackerState.visitNumber });
  reachGoal("checkout_open", { tier, visit_number: trackerState.visitNumber });
}

export function trackCheckoutClose(): void {
  if (trackerState.checkoutOpenTs > 0) {
    queueEvent("checkout_close", {
      duration_ms: Date.now() - trackerState.checkoutOpenTs,
    });
    trackerState.checkoutOpenTs = 0;
  }
}

export function trackFormStart(formVariant?: string): void {
  trackerState.formStartTs = Date.now();
  queueEvent("form_start", withFormVariant({}, formVariant));
  // Also a Metrika goal so it can join engaged_30s in the composite Direct proxy.
  reachGoal("form_start", withFormVariant({}, formVariant));
}

export function trackFormFieldFocus(field: string, formVariant?: string): void {
  queueEvent("form_field_focus", withFormVariant({ field }, formVariant));
}

export function trackFormFieldError(field: string, errorType: string, formVariant?: string): void {
  queueEvent("form_field_error", withFormVariant({ field, error_type: errorType }, formVariant));
}

export function trackFormAbandon(
  fields: {
    name_filled: boolean;
    email_filled: boolean;
    consents_checked: number;
    consents_total: number;
  },
  formVariant?: string,
): void {
  queueEvent("form_abandon", withFormVariant({ ...fields }, formVariant));
  // Fired on pagehide/visibilitychange-hidden, so it must ship immediately via
  // sendBeacon — a queued-only event would be lost on unload (same reason
  // trackRegistrationSubmit flushes now).
  flushNow();
}

export function trackFormSubmit(offerId: string, tier: string): void {
  const duration = trackerState.formStartTs > 0 ? Date.now() - trackerState.formStartTs : undefined;
  queueEvent("form_submit", { offer_id: offerId, tier, duration_ms: duration });
  reachGoal("form_submit", { tier, offer_id: offerId });
  trackerState.checkoutOpenTs = 0;
  // Flush immediately: the checkout form redirects to the payment provider on
  // success, so a queued form_submit would be lost before the 2s batch timer.
  flushNow();
}

export function trackRegistrationSubmit(eventSlug: string, formVariant?: string): void {
  queueEvent("registration_submit", withFormVariant({ event_slug: eventSlug }, formVariant));
  reachGoal("registration_submit", withFormVariant({ event_slug: eventSlug }, formVariant));
  // Flush immediately: lead-magnet/webinar forms redirect to the thank-you /
  // check-your-inbox page on success, so a queued event would be lost before the
  // 2s batch timer. This is why registration_submit never reached the collector.
  flushNow();
}

/**
 * Post-submit "what's next" page landing (lead-magnet thank-you /
 * check-your-inbox). Records that a visitor reached the next-steps page, tagged
 * with the lead-magnet `source` they came from and the page `mode`
 * (confirm|delivered), so upsell-card clicks (fired as `section_interact` via
 * `data-track` on each card) can be attributed to the originating funnel by
 * `session_id`. Uses the allowlisted `section_interact` type — no collector
 * change. Flushed immediately: the visitor may click a card (navigating away)
 * before the 2s batch timer, same reason `registration_submit` flushes now.
 */
export function trackLeadMagnetNext(source: string, mode: string): void {
  queueEvent("section_interact", {
    target: `lm_next.view.${mode}`,
    section_name: "lead-magnet-next-steps",
    section_role: "closer",
    ...(source ? { source } : {}),
    mode,
  });
  flushNow();
}

/**
 * Native website quiz (ADR-0093) — progressive, `visitor_id`-keyed first-party
 * capture for EVERYONE who touches the quiz, incl. abandoners and
 * non-registrants. The four `quiz_*` event types are pre-declared in the shared
 * `EVENT_TYPES` allowlist. PII rule (hard): only non-PII answers may be passed
 * here — email/name NEVER enter the anonymous event store (they live only on the
 * identified opt_ins/deal side). `quiz_start`/`quiz_answer` ride the 2s batch +
 * the pagehide/visibilitychange `flushNow()` beacon (so an abandoned quiz still
 * lands its answered steps); `quiz_complete`/`quiz_cta_click` flush immediately.
 */
export function trackQuizStart(quizId: string): void {
  queueEvent("quiz_start", { quiz_id: quizId });
}

export function trackQuizAnswer(payload: {
  quizId: string;
  questionId: string;
  answer: string | string[];
  stepIndex: number;
  archetypeSoFar?: string;
}): void {
  queueEvent("quiz_answer", {
    quiz_id: payload.quizId,
    question_id: payload.questionId,
    answer: payload.answer,
    step_index: payload.stepIndex,
    ...(payload.archetypeSoFar ? { archetype_so_far: payload.archetypeSoFar } : {}),
  });
}

export function trackQuizAnswerImmediately(payload: Parameters<typeof trackQuizAnswer>[0]): void {
  trackQuizAnswer(payload);
  flushNow();
}

export function trackQualificationComplete(payload: {
  quizId: string;
  answers: Record<string, string>;
}): void {
  queueEvent("quiz_complete", {
    quiz_id: payload.quizId,
    answers: payload.answers,
  });
  flushNow();
}

export function trackQuizComplete(payload: {
  quizId: string;
  symptom: string;
  cluster?: string;
}): void {
  queueEvent("quiz_complete", {
    quiz_id: payload.quizId,
    symptom: payload.symptom,
    ...(payload.cluster ? { cluster: payload.cluster } : {}),
  });
  // Fired at the email-gate submit (registration) — flush now so it isn't lost
  // if the tab is backgrounded before the 2s batch timer.
  flushNow();
}

export function trackQuizCtaClick(payload: {
  quizId: string;
  symptom: string;
  target: string;
}): void {
  queueEvent("quiz_cta_click", {
    quiz_id: payload.quizId,
    symptom: payload.symptom,
    target: payload.target,
  });
  // The result CTA may navigate away, so ship immediately.
  flushNow();
}
